'use client';

import Link from 'next/link';

import { BadgeCheck, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { getTrialPeriodDays } from '@kit/shared/config';
import { cn } from '@kit/ui/utils';

import { useSectionVisibility } from '~/lib/contexts/section-visibility-context';
import { formatDays } from '~/lib/plans/pilot';

// User type labels for display
const userTypeLabels: Record<string, string> = {
  doctor: 'Lekarz',
  pharmacist: 'Farmaceuta',
  nurse_midwife: 'Pielęgniarka / Położna',
  pharma: 'Apteka',
  medical_place: 'Placówka medyczna',
  hospital: 'Szpital',
  partner: 'Partner',
};

export interface AccountStatusBarProps {
  hasProfile: boolean;
  trialEndsAt: Date | null;
  verified: boolean;
  subscriptionEndsAt?: Date | null;
  /** Pilot length in days; its clock starts on the first interaction check */
  pilotDays?: number | null;
  profileName?: string;
  userType?: string | null;
  email?: string;
  onActivate: () => void;
  /** Render in compact mode for dropdown menu */
  compact?: boolean;
  /** Whether the current user is a super admin */
  isSuperAdmin?: boolean;
}

export function AccountStatusBar({
  hasProfile,
  trialEndsAt,
  verified,
  subscriptionEndsAt,
  pilotDays,
  profileName,
  userType,
  email,
  onActivate,
  compact = false,
  isSuperAdmin = false,
}: AccountStatusBarProps) {
  const { t } = useTranslation('account');

  // No profile yet - show activate button with neubrutalism style
  if (!hasProfile) {
    if (compact) {
      return (
        <button
          onClick={onActivate}
          className="w-full rounded-md border-2 border-green-600 bg-green-700 px-3 py-1.5 text-left text-xs font-medium text-white shadow-[2px_2px_0_0_#16a34a] transition-all hover:translate-x-[1px] hover:translate-y-[1px] hover:bg-green-800 hover:shadow-[1px_1px_0_0_#16a34a] dark:border-green-400 dark:bg-green-700 dark:shadow-[2px_2px_0_0_#22c55e] dark:hover:bg-green-800"
        >
          {t('profileWizard.activateTrial', { days: getTrialPeriodDays() })}
        </button>
      );
    }
    return (
      <button
        onClick={onActivate}
        className="hidden rounded-md border-2 border-green-600 bg-green-700 px-3 py-1.5 text-xs font-medium text-white shadow-[2px_2px_0_0_#16a34a] transition-all hover:translate-x-[1px] hover:translate-y-[1px] hover:bg-green-800 hover:shadow-[1px_1px_0_0_#16a34a] @lg:block dark:border-green-400 dark:bg-green-700 dark:shadow-[2px_2px_0_0_#22c55e] dark:hover:bg-green-800"
      >
        {t('profileWizard.activateTrial', { days: getTrialPeriodDays() })}
      </button>
    );
  }

  // Determine status type and format date if needed
  let statusIcon: React.ReactNode;
  let statusText: string;
  let statusColor: string;

  if (verified) {
    // Active subscription
    if (!subscriptionEndsAt) {
      // Unlimited subscription - just show "subskrypcja aktywna"; a pilot
      // waiting for its first use shows its length instead of an end date
      statusIcon = <BadgeCheck className="h-3 w-3" />;
      statusText = pilotDays
        ? `pilotaż · ${formatDays(pilotDays)}`
        : 'subskrypcja aktywna';
      statusColor = 'text-green-600 dark:text-green-400';
    } else {
      // Time-limited subscription
      const now = new Date();
      const daysRemaining = Math.ceil(
        (subscriptionEndsAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
      );

      if (daysRemaining <= 0) {
        // Subscription expired
        statusIcon = <Clock className="h-3 w-3" />;
        statusText = 'subskrypcja wygasła';
        statusColor = 'text-red-600 dark:text-red-400';
      } else if (daysRemaining <= 7) {
        // Expiring soon
        const formattedDate = subscriptionEndsAt.toLocaleDateString('pl-PL', {
          day: '2-digit',
          month: '2-digit',
        });
        statusIcon = <Clock className="h-3 w-3" />;
        statusText = `subskrypcja do ${formattedDate}`;
        statusColor = 'text-amber-600 dark:text-amber-400';
      } else {
        // Active with future end date
        const formattedDate = subscriptionEndsAt.toLocaleDateString('pl-PL', {
          day: '2-digit',
          month: '2-digit',
        });
        statusIcon = <BadgeCheck className="h-3 w-3" />;
        statusText = `subskrypcja do ${formattedDate}`;
        statusColor = 'text-green-600 dark:text-green-400';
      }
    }
  } else if (trialEndsAt) {
    const now = new Date();
    const daysRemaining = Math.ceil(
      (trialEndsAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
    );

    if (daysRemaining <= 0) {
      statusIcon = <Clock className="h-3 w-3" />;
      statusText = 'okres próbny zakończony';
      statusColor = 'text-red-600 dark:text-red-400';
    } else {
      const formattedDate = trialEndsAt.toLocaleDateString('pl-PL', {
        day: '2-digit',
        month: '2-digit',
      });

      statusIcon = <Clock className="h-3 w-3" />;
      statusText = `okres próbny do ${formattedDate}`;
      statusColor = 'text-blue-600 dark:text-blue-400';
    }
  } else {
    return null;
  }

  const userTypeLabel = isSuperAdmin
    ? 'Administrator'
    : userType
      ? userTypeLabels[userType]
      : null;

  // Compact mode for mobile dropdown menu
  if (compact) {
    return (
      <div className="flex flex-col items-start text-xs">
        <div className="flex items-baseline gap-1.5">
          {profileName && (
            <span className="text-foreground font-medium">{profileName}</span>
          )}
          {userTypeLabel &&
            (isSuperAdmin ? (
              <Link
                href="/admin"
                className="text-[10px] tracking-wide text-yellow-600 uppercase underline-offset-2 hover:underline dark:text-yellow-400"
              >
                · {userTypeLabel}
              </Link>
            ) : (
              <span className="text-muted-foreground text-[10px] tracking-wide uppercase">
                · {userTypeLabel}
              </span>
            ))}
        </div>
        {email && (
          <div className="text-muted-foreground truncate text-[11px]">
            {email}
          </div>
        )}
        <StatusLine
          className={`flex items-center gap-1 ${statusColor}`}
          linkClassName="cursor-pointer hover:underline"
        >
          {statusIcon}
          <span className="text-[10px] tracking-wide whitespace-nowrap uppercase">
            {statusText}
          </span>
        </StatusLine>
      </div>
    );
  }

  // Desktop header - 2 rows: name+type on top, status below
  // Uses @lg container query (512px) so it hides when header container is narrow (e.g., PDF viewer open)
  return (
    <div className="hidden flex-col items-end text-xs @lg:flex">
      {isSuperAdmin ? (
        <div className="flex items-baseline gap-1.5">
          {profileName && (
            <Link
              href="/home/settings"
              className="text-foreground font-medium underline-offset-2 hover:underline"
            >
              {profileName}
            </Link>
          )}
          {userTypeLabel && (
            <Link
              href="/admin"
              className="text-[10px] tracking-wide text-yellow-600 uppercase underline-offset-2 hover:underline dark:text-yellow-400"
            >
              · {userTypeLabel}
            </Link>
          )}
        </div>
      ) : (
        <Link
          href="/home/settings"
          className="hover:text-foreground inline-flex items-baseline gap-1.5 rounded-sm px-1.5 py-0.5 transition-all hover:bg-zinc-100 dark:hover:bg-zinc-800"
        >
          {profileName && (
            <span className="text-foreground font-medium">{profileName}</span>
          )}
          {userTypeLabel && (
            <span className="text-muted-foreground text-[10px] tracking-wide uppercase">
              · {userTypeLabel}
            </span>
          )}
        </Link>
      )}
      <StatusLine
        className={`flex items-center gap-1 rounded-sm px-1.5 py-0.5 ${statusColor}`}
        linkClassName="cursor-pointer transition-all hover:bg-zinc-100 dark:hover:bg-zinc-800"
      >
        {statusIcon}
        <span className="text-[10px] tracking-wide uppercase">
          {statusText}
        </span>
      </StatusLine>
    </div>
  );
}

/**
 * Trial/subscription status line. Links to /pricing only while the
 * `nav_pricing` section is enabled; otherwise renders as plain text.
 */
function StatusLine({
  className,
  linkClassName,
  children,
}: React.PropsWithChildren<{ className: string; linkClassName: string }>) {
  const { nav_pricing } = useSectionVisibility();

  if (!nav_pricing) {
    return <div className={className}>{children}</div>;
  }

  return (
    <Link href="/pricing" className={cn(className, linkClassName)}>
      {children}
    </Link>
  );
}
